import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Lock, FileText, Download, ArrowLeft, Pencil, Trash2, Send, LogIn } from 'lucide-react';
import { useSelector } from 'react-redux';
import { authSelector } from '../../features/auth/slice';
import axios from 'axios';
import PublicLayout from '../../components/public/PublicLayout';
import { PUBLIC_BASE } from './config';
import UserAvatar from '../../components/UserAvatar';
import ProfilePopover from '../../components/ProfilePopover';

const BOARD_PATHS = {
  PNotice: 'notice',
  PNews: 'news',
  PFree: 'free',
};

const BOARD_LABELS = {
  PNotice: '공지사항',
  PNews: '뉴스',
  PFree: '자유게시판',
};

function PublicBoardViewPage({ boardType = 'PNotice' }) {
  const navigate = useNavigate();
  const { me } = useSelector(authSelector.selectAuthState);
  const { id } = useParams();
  const [article, setArticle] = useState({});
  const [file, setFile] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentCount, setCommentCount] = useState(0);
  const [parentContent, setParentContent] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [commentUpdate, setCommentUpdate] = useState(true);
  const [showLoginAlert, setShowLoginAlert] = useState(false);

  const boardPath = BOARD_PATHS[boardType];

  const getAuthorInfo = (item) => {
    if (item?.client) {
      return {
        name: item.client.nickName || item.client.name,
        profileImage: item.client.profileImage,
        userType: 'client',
        adminYn: item.client.adminYn,
      };
    }
    if (item?.member) {
      return {
        name: item.member.nickName || item.member.name,
        profileImage: item.member.profileImage,
        userType: 'member',
        adminYn: 'N',
      };
    }
    return { name: item?.writer || '알 수 없음', profileImage: null, userType: 'member', adminYn: 'N' };
  };

  useEffect(() => {
    axios
      .get(`/api/public/board/${id}?boardType=${boardType}`, { withCredentials: true })
      .then((response) => {
        setArticle(response.data.article);
        setFile(response.data.file);
      });
    axios
      .get(`/api/public/board/comment/${id}?boardType=${boardType}`, { withCredentials: true })
      .then((response) => {
        setComments(response.data);
        const parentOnly = response.data.filter((c) => !c.parent);
        setCommentCount(parentOnly.length);
      });
  }, [id, commentUpdate, boardType]);

  const deleteArticles = () => {
    if (window.confirm('게시물을 삭제하시겠습니까?')) {
      axios
        .delete(`/api/public/board/${id}?boardType=${boardType}`, { withCredentials: true })
        .then(() => {
          navigate(`${PUBLIC_BASE}/community/${boardPath}`);
        });
    }
  };

  const newParentComment = () => {
    if (!me) {
      setShowLoginAlert(true);
      return;
    }
    if (!parentContent.trim()) {
      alert('댓글 내용을 입력해 주세요.');
      return;
    }
    setCommentLoading(true);
    const writerName = me.nickName || me.name;
    const parentComment = {
      boardId: id,
      clientId: me.userType === 'client' ? me.id : null,
      memberId: me.userType === 'member' ? me.id : null,
      writer: writerName,
      content: parentContent,
      boardType,
    };

    axios
      .post('/api/public/board/comment/write', parentComment, { withCredentials: true })
      .then((response) => {
        const newComment = response.data;
        const copyComments = [...comments, newComment];
        setComments(copyComments);
        setCommentCount(copyComments.filter((c) => !c.parent).length);
        setParentContent('');
      })
      .catch(() => {
        alert('댓글 등록 중 오류가 발생했습니다.');
      })
      .finally(() => setCommentLoading(false));
  };

  const isOwner =
    (me?.userType === 'client' && (me?.id === article?.clientId || me?.adminYn === 'Y')) ||
    (me?.userType === 'member' && me?.id === article?.memberId);
  const isAdmin = me?.adminYn === 'Y';

  const articleAuthor = getAuthorInfo(article);

  const parentComments = comments
    .filter((comment) => !comment.parent)
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  return (
    <PublicLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* 게시글 카드 */}
        <div className="rounded-2xl border border-white/[0.08] bg-[#182334] overflow-hidden">
          {/* 헤더 */}
          <div className="px-6 py-5 border-b border-white/[0.08]">
            <div className="flex items-start gap-2 mb-3">
              {article.secretYn === 'Y' && (
                <Lock size={16} className="text-slate-500 mt-0.5 shrink-0" />
              )}
              <h1 className="text-lg font-bold text-white leading-snug">{article.title}</h1>
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-400">
              <ProfilePopover user={{
                userType: articleAuthor.userType,
                userId: article.client?.id || article.member?.id,
                name: articleAuthor.name,
                profileImage: articleAuthor.profileImage,
                adminYn: articleAuthor.adminYn,
              }}>
                <UserAvatar
                  name={articleAuthor.name}
                  profileImage={articleAuthor.profileImage}
                  userType={articleAuthor.userType}
                  adminYn={articleAuthor.adminYn}
                  size={22}
                />
                <span>{articleAuthor.name}</span>
              </ProfilePopover>
              <span>{article.createdAt && new Date(article.createdAt).toLocaleDateString('ko-KR')}</span>
              {article.categoryName && (
                <span className="rounded-full border px-2 py-0.5 font-medium border-sky-500/20 bg-sky-500/10 text-sky-400">
                  {article.categoryName}
                </span>
              )}
            </div>
          </div>

          {/* 본문 */}
          <div className="p-6">
            {file?.fileUrl && (
              <div className="mb-5">
                {file.fileUrl.match(/\.(jpeg|jpg|png|gif|bmp|webp)$/i) ? (
                  <img
                    src={file.fileUrl}
                    alt="첨부 이미지"
                    className="max-w-full h-auto rounded-xl border border-white/[0.08]"
                  />
                ) : file.fileUrl.match(/\.(mp3|wav|ogg)$/i) ? (
                  <audio controls className="w-full">
                    <source src={file.fileUrl} />
                  </audio>
                ) : (
                  <a
                    href={file.fileUrl}
                    download
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.08] text-slate-300 hover:bg-white/[0.06] hover:text-white transition-colors text-sm">
                    <FileText size={16} className="text-slate-400" />
                    <span className="truncate max-w-[200px]">{file.fileUrl.split('/').pop()}</span>
                    <Download size={14} className="text-slate-500 ml-1" />
                  </a>
                )}
              </div>
            )}
            <div
              className="board-content prose max-w-none text-sm leading-relaxed bg-white rounded-xl p-5"
              dangerouslySetInnerHTML={{ __html: article.description }}
            />
          </div>
        </div>

        {/* 액션 버튼 */}
        <div className="flex items-center justify-between mt-4">
          <button
            onClick={() => navigate(`${PUBLIC_BASE}/community/${boardPath}`)}
            className="flex items-center gap-1.5 px-4 py-2 text-slate-400 hover:text-white text-sm font-medium transition-colors">
            <ArrowLeft size={16} />
            목록
          </button>
          {(isOwner || isAdmin) && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(`${PUBLIC_BASE}/community/${boardPath}/modify/${id}`)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-white text-sm font-semibold bg-sky-500 hover:bg-sky-600 transition-colors">
                <Pencil size={14} />
                수정
              </button>
              <button
                onClick={deleteArticles}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-white text-sm font-semibold bg-rose-500 hover:bg-rose-600 transition-colors">
                <Trash2 size={14} />
                삭제
              </button>
            </div>
          )}
        </div>

        {/* 댓글 섹션 */}
        <div className="mt-6 rounded-2xl border border-white/[0.08] bg-[#182334] overflow-hidden">
          <div className="px-6 py-4 bg-white/[0.03] border-b border-white/[0.08] flex items-center gap-2">
            <span className="text-slate-200 text-sm font-semibold">댓글</span>
            <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full bg-sky-500/15 text-sky-400">
              {commentCount}
            </span>
          </div>
          <div className="p-6 space-y-4">
            {/* 댓글 작성 */}
            {me ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <UserAvatar
                    name={me.nickName || me.name}
                    profileImage={me.profileImage}
                    userType={me.userType}
                    adminYn={me.adminYn}
                    size={28}
                  />
                  <span className="text-sm font-medium text-slate-200">
                    {me.nickName || me.name}
                  </span>
                </div>
                <textarea
                  className="w-full h-24 p-3 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-transparent resize-none placeholder-slate-500 transition"
                  value={parentContent}
                  onChange={(e) => setParentContent(e.target.value)}
                  placeholder="댓글을 입력하세요"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      newParentComment();
                    }
                  }}
                />
                <div className="flex items-center justify-between">
                  <span className="text-[11px] text-slate-600">Ctrl+Enter로 등록</span>
                  <button
                    onClick={newParentComment}
                    disabled={commentLoading || !parentContent.trim()}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
                      commentLoading || !parentContent.trim()
                        ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
                        : 'bg-sky-500 hover:bg-sky-600 text-white'
                    }`}>
                    <Send size={13} />
                    {commentLoading ? '등록 중...' : '등록'}
                  </button>
                </div>
              </div>
            ) : (
              <div
                className="relative cursor-pointer"
                onClick={() => setShowLoginAlert(true)}>
                <div className="rounded-xl border border-white/[0.08] bg-[#1a2b42]/50 p-6 flex flex-col items-center gap-2">
                  <LogIn size={20} className="text-slate-500" />
                  <p className="text-sm text-slate-500">로그인 후 댓글을 작성할 수 있습니다.</p>
                </div>
              </div>
            )}

            {/* 댓글 목록 */}
            {parentComments.length > 0 && (
              <div className="pt-4 border-t border-white/[0.06] space-y-3">
                {parentComments.map((parentComment) => (
                  <PublicCommentItem
                    key={parentComment.id}
                    comments={comments}
                    boardId={id}
                    boardType={boardType}
                    setComments={setComments}
                    setCommentCount={setCommentCount}
                    setCommentUpdate={setCommentUpdate}
                    me={me}
                    parentComment={parentComment}
                    onLoginRequired={() => setShowLoginAlert(true)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 로그인 알림 모달 */}
      {showLoginAlert && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-[#1a2b42] border border-white/10 rounded-2xl p-6 max-w-sm mx-4 shadow-2xl">
            <p className="text-slate-200 text-sm text-center mb-5">
              댓글을 작성하려면 로그인이 필요합니다.
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowLoginAlert(false)}
                className="flex-1 px-4 py-2.5 rounded-xl border border-white/[0.08] text-slate-400 text-sm font-semibold hover:bg-white/5 transition-colors">
                취소
              </button>
              <button
                onClick={() => {
                  setShowLoginAlert(false);
                  navigate(`${PUBLIC_BASE}/login`);
                }}
                className="flex-1 px-4 py-2.5 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-semibold transition-colors">
                로그인
              </button>
            </div>
          </div>
        </div>
      )}
    </PublicLayout>
  );
}

/* ── Comment Components ── */

function PublicCommentItem({
  comments,
  boardId,
  boardType,
  setComments,
  setCommentCount,
  setCommentUpdate,
  me,
  parentComment,
  onLoginRequired,
}) {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [childContent, setChildContent] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(parentComment?.content || '');

  const getCommentAuthor = (comment) => {
    if (comment.client) {
      return {
        name: comment.client.nickName || comment.client.name,
        profileImage: comment.client.profileImage,
        userType: 'client',
        adminYn: comment.client.adminYn,
      };
    }
    if (comment.member) {
      return {
        name: comment.member.nickName || comment.member.name,
        profileImage: comment.member.profileImage,
        userType: 'member',
        adminYn: 'N',
      };
    }
    return { name: comment.writer || '알 수 없음', profileImage: null, userType: 'member', adminYn: 'N' };
  };

  const author = getCommentAuthor(parentComment);

  const isMyComment =
    (me?.userType === 'client' && (me?.id === Number(parentComment?.clientId) || me?.adminYn === 'Y')) ||
    (me?.userType === 'member' && me?.id === Number(parentComment?.memberId));

  const childCommentList = comments
    .filter((c) => c.parent === parentComment?.id)
    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

  const handleReply = () => {
    if (!me) {
      onLoginRequired();
      return;
    }
    if (!childContent.trim()) return;
    const writerName = me.nickName || me.name;
    const childComment = {
      boardId,
      clientId: me.userType === 'client' ? me.id : null,
      memberId: me.userType === 'member' ? me.id : null,
      writer: writerName,
      content: childContent,
      parent: parentComment.id,
      boardType,
    };
    axios
      .post('/api/public/board/comment/write', childComment, { withCredentials: true })
      .then((response) => {
        setComments((prev) => [...prev, response.data]);
        setChildContent('');
        setShowReplyForm(false);
      })
      .catch(() => alert('대댓글 등록 중 오류가 발생했습니다.'));
  };

  const handleEdit = () => {
    if (!editContent.trim()) return;
    axios
      .put(`/api/public/board/comment/modify/${parentComment.id}`, { content: editContent, boardType }, { withCredentials: true })
      .then(() => {
        setCommentUpdate((prev) => !prev);
        setIsEditing(false);
      });
  };

  const handleDelete = () => {
    if (!window.confirm('댓글을 삭제하시겠습니까?')) return;
    axios
      .delete(`/api/public/board/comment/${parentComment.id}?boardType=${boardType}`, { withCredentials: true })
      .then(() => {
        const updatedComments = comments.filter((c) => c.id !== parentComment.id);
        setComments(updatedComments);
        setCommentCount(updatedComments.filter((c) => !c.parent).length);
      });
  };

  return (
    <div className="rounded-xl border border-white/[0.08] overflow-hidden">
      <div className="px-4 py-3 bg-[#182334]">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <ProfilePopover user={{
              userType: author.userType,
              userId: parentComment.client?.id || parentComment.member?.id,
              name: author.name,
              profileImage: author.profileImage,
              adminYn: author.adminYn,
            }}>
              <UserAvatar
                name={author.name}
                profileImage={author.profileImage}
                userType={author.userType}
                adminYn={author.adminYn}
                size={24}
              />
              <span className="text-sm font-semibold text-slate-200">{author.name}</span>
            </ProfilePopover>
            <span className="text-[11px] text-slate-500">
              {new Date(parentComment?.createdAt).toLocaleDateString('ko-KR')}
            </span>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {me && (
              <button
                className="p-1 text-slate-500 hover:text-sky-400 transition-colors text-xs"
                onClick={() => {
                  if (!me) { onLoginRequired(); return; }
                  setShowReplyForm(!showReplyForm);
                }}
                title="대댓글">
                답글
              </button>
            )}
            {isMyComment && !isEditing && (
              <>
                <button
                  className="p-1 text-slate-500 hover:text-sky-400 transition-colors text-xs"
                  onClick={() => setIsEditing(true)}
                  title="수정">
                  수정
                </button>
                <button
                  className="p-1 text-slate-500 hover:text-rose-400 transition-colors text-xs"
                  onClick={handleDelete}
                  title="삭제">
                  삭제
                </button>
              </>
            )}
          </div>
        </div>

        <div className="mt-2 ml-8">
          {isEditing ? (
            <div className="flex items-center gap-2">
              <input
                className="flex-1 px-3 py-1.5 rounded-lg border border-white/[0.08] text-sm outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-transparent bg-[#1a2b42] text-slate-200"
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleEdit()}
                autoFocus
              />
              <button
                className="px-2 py-1 text-xs text-sky-400 hover:text-sky-300 transition-colors"
                onClick={handleEdit}>
                완료
              </button>
              <button
                className="px-2 py-1 text-xs text-slate-500 hover:text-slate-300 transition-colors"
                onClick={() => { setIsEditing(false); setEditContent(parentComment?.content || ''); }}>
                취소
              </button>
            </div>
          ) : (
            <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">
              {parentComment?.content}
            </p>
          )}
        </div>
      </div>

      {/* 대댓글 작성 폼 */}
      {showReplyForm && (
        <div className="ml-8 px-4 py-3 border-t border-white/[0.06] bg-white/[0.02] flex items-center gap-3">
          <span className="text-xs font-medium text-slate-500 shrink-0">
            {me?.nickName || me?.name}
          </span>
          <input
            className="flex-1 px-3 py-1.5 rounded-lg border border-white/[0.08] text-sm outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-transparent bg-[#1a2b42] text-slate-200 placeholder-slate-500"
            value={childContent}
            onChange={(e) => setChildContent(e.target.value)}
            placeholder="대댓글을 입력하세요"
            onKeyDown={(e) => e.key === 'Enter' && handleReply()}
            autoFocus
          />
          <button
            className="shrink-0 px-3 py-1.5 bg-sky-500 rounded-lg hover:bg-sky-600 text-white text-xs font-semibold transition-colors disabled:bg-slate-700 disabled:text-slate-500"
            onClick={handleReply}
            disabled={!childContent.trim()}>
            등록
          </button>
        </div>
      )}

      {/* 대댓글 목록 */}
      {childCommentList.map((childComment) => {
        const childAuthor = getCommentAuthor(childComment);
        return (
          <PublicChildCommentItem
            key={childComment.id}
            comments={comments}
            setComments={setComments}
            me={me}
            boardType={boardType}
            childComment={childComment}
            childAuthor={childAuthor}
            setCommentUpdate={setCommentUpdate}
          />
        );
      })}
    </div>
  );
}

function PublicChildCommentItem({
  comments,
  setComments,
  me,
  boardType,
  childComment,
  childAuthor,
  setCommentUpdate,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(childComment.content);

  const isMyComment =
    (me?.userType === 'client' && (me?.id === Number(childComment.clientId) || me?.adminYn === 'Y')) ||
    (me?.userType === 'member' && me?.id === Number(childComment.memberId));

  const handleEdit = () => {
    if (!editContent.trim()) return;
    axios
      .put(`/api/public/board/comment/modify/${childComment.id}`, { content: editContent, boardType }, { withCredentials: true })
      .then(() => {
        setCommentUpdate((prev) => !prev);
        setIsEditing(false);
      });
  };

  const handleDelete = () => {
    if (!window.confirm('댓글을 삭제하시겠습니까?')) return;
    axios
      .delete(`/api/public/board/comment/${childComment.id}?boardType=${boardType}`, { withCredentials: true })
      .then(() => {
        setComments((prev) => prev.filter((c) => c.id !== childComment.id));
      });
  };

  return (
    <div className="ml-8 px-4 py-2.5 border-t border-white/[0.06] bg-white/[0.02]">
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <ProfilePopover user={{
              userType: childAuthor.userType,
              userId: childComment.client?.id || childComment.member?.id,
              name: childAuthor.name,
              profileImage: childAuthor.profileImage,
              adminYn: childAuthor.adminYn,
            }}>
              <UserAvatar
                name={childAuthor.name}
                profileImage={childAuthor.profileImage}
                userType={childAuthor.userType}
                adminYn={childAuthor.adminYn}
                size={20}
              />
              <span className="text-xs font-semibold text-slate-300">{childAuthor.name}</span>
            </ProfilePopover>
            <span className="text-[11px] text-slate-600">
              {new Date(childComment.createdAt).toLocaleDateString('ko-KR')}
            </span>
          </div>
          {isEditing ? (
            <div className="flex items-center gap-2 mt-1">
              <input
                className="flex-1 px-3 py-1 rounded-lg border border-white/[0.08] text-sm outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-transparent bg-[#1a2b42] text-slate-200"
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleEdit()}
                autoFocus
              />
              <button className="px-2 py-1 text-xs text-sky-400 hover:text-sky-300" onClick={handleEdit}>완료</button>
              <button className="px-2 py-1 text-xs text-slate-500 hover:text-slate-300" onClick={() => { setIsEditing(false); setEditContent(childComment.content); }}>취소</button>
            </div>
          ) : (
            <p className="text-sm text-slate-400 mt-0.5 whitespace-pre-wrap">{childComment.content}</p>
          )}
        </div>
        {isMyComment && !isEditing && (
          <div className="flex items-center gap-0.5 shrink-0">
            <button className="p-1 text-slate-600 hover:text-sky-400 transition-colors text-xs" onClick={() => setIsEditing(true)}>수정</button>
            <button className="p-1 text-slate-600 hover:text-rose-400 transition-colors text-xs" onClick={handleDelete}>삭제</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default PublicBoardViewPage;
